import { DEFAULT_THEME, MantineTheme } from "@mantine/core";

const colorPalette = {
  grey: {
    0: "#FFFFFF",
    50: "#F8F8F8",
    100: "#F6F6F7",
    200: "#F2F2F2",
    300: "#EAEBEB",
    400: "#DCDDDE",
    500: "#BFC1C3",
    600: "#898C8F",
    700: "#666B6E",
    800: "#4C5255",
    900: "#394044",
    1000: "#1B2328",
    1100: "#0A1217",
  },
  green: {
    100: "#F1FAF2",
    200: "#E6F6E9",
    300: "#DCF1E1",
    400: "#C3E6CC",
    500: "#8DD19E",
    600: "#12A139",
    700: "#038026",
    800: "#015F1A",
    900: "#104B20",
    1000: "#112716",
  },
  blue: {
    100: "#F0F8FF",
    200: "#E8F3FE",
    300: "#DDEDFE",
    400: "#C6E0FE",
    500: "#94C5FD",
    600: "#318CF7",
    700: "#166BD8",
    800: "#0D4DAA",
    900: "#173F7F",
    1000: "#162339",
  },
  red: {
    100: "#FFF5F3",
    200: "#FFEFEC",
    300: "#FFE6E2",
    400: "#FFD3CD",
    500: "#FFACA5",
    600: "#F84A43",
    700: "#D7251C",
    800: "#A3150F",
    900: "#772320",
    1000: "#381918",
  },
  orange: {
    100: "#FEF6EC",
    200: "#FDF0E1",
    300: "#FDE8D4",
    400: "#FBD8BB",
    500: "#F7B383",
    600: "#EB6100",
    700: "#BE4900",
    800: "#8F3400",
    900: "#712A05",
    1000: "#371C10",
  },
  purple: {
    100: "#F7F6FF",
    200: "#F3F1FF",
    300: "#ECE9FE",
    400: "#DED9FE",
    500: "#C2B9FE",
    600: "#8C78FF",
    700: "#6E57E4",
    800: "#503EB2",
    900: "#413582",
    1000: "#221F38",
  },
  black: {
    1.6: "rgba(0, 0, 0, 0.016)",
    2: "rgba(0, 0, 0, 0.02)",
    4: "rgba(0, 0, 0, 0.04)",
    5: "rgba(0, 0, 0, 0.05)",
    6: "rgba(0, 0, 0, 0.06)",
    7: "rgba(0, 0, 0, 0.07)",
    8: "rgba(0, 0, 0, 0.08)",
    10: "rgba(0, 0, 0, 0.10)",
    12: "rgba(0, 0, 0, 0.12)",
    20: "rgba(0, 0, 0, 0.20)",
    28: "rgba(0, 0, 0, 0.28)",
    34: "rgba(0, 0, 0, 0.34)",
  },
  magenta: {
    100: "#FDF5FB",
    200: "#FBEFF8",
    300: "#FAE6F5",
    400: "#F5D3EC",
    500: "#EFADDE",
    600: "#E54EC1",
    700: "#BD31A1",
    800: "#901E7C",
    900: "#6A255E",
    1000: "#311B2D",
  },
  beige: {
    100: "#F7F6F2",
    200: "#F4F2EC",
    300: "#EEEBE5",
    400: "#DFDCD6",
    500: "#C5C1BB",
    600: "#8F8B87",
    700: "#6E6A66",
    800: "#53514E",
    900: "#413F3D",
    1000: "#232221",
  },
};

// Kept out of colorPalette: every colorPalette entry becomes a Mantine color that needs ~10 shades,
// a single-shade "white" makes MantineCssVariables crash on undefined shades.
const whitePalette = {
  60: "rgba(255, 255, 255, 0.60)",
};

export const shadowsPalette = colorPalette.black;

export const mantineColors: MantineTheme["colors"] = {
  ...DEFAULT_THEME.colors,
  ...Object.fromEntries(
    Object.entries(colorPalette).map(([colorKey, colorObject]) => [
      colorKey,
      Object.entries(colorObject)
        .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
        .map(([_key, value]) => value),
    ]),
  ),
};

export const colors = {
  text: {
    primaryDefault: colorPalette.grey[1000],
    primaryHover: colorPalette.grey[1100],
    primaryDisabled: colorPalette.grey[500],
    primaryInverse: colorPalette.grey[0],
    secondaryDefault: colorPalette.grey[700],
    secondaryDisabled: colorPalette.grey[500],
    secondaryInverse: colorPalette.grey[600],
    navDefault: colorPalette.grey[900],
    navHover: colorPalette.grey[1000],
    errorDefault: colorPalette.red[800],
    errorDisabled: colorPalette.grey[0],
    success: colorPalette.green[800],
    warning: colorPalette.orange[800],
    info: colorPalette.blue[800],
    neutral: colorPalette.grey[800],
    alternative: colorPalette.purple[800],
  },
  icon: {
    primaryDefault: colorPalette.grey[1000],
    primaryHover: colorPalette.grey[1100],
    primaryPressed: colorPalette.grey[1100],
    primaryDisabled: colorPalette.grey[500],
    primaryInverse: colorPalette.grey[0],
    secondary: colorPalette.grey[700],
    secondaryInverse: colorPalette.grey[600],
    tertiary: colorPalette.grey[500],
    errorDefault: colorPalette.red[700],
    errorDisabled: colorPalette.grey[0],
    success: colorPalette.green[600],
    warning: colorPalette.orange[600],
    info: colorPalette.blue[600],
    avatar: {
      green: colorPalette.green[800],
      blue: colorPalette.blue[800],
      orange: colorPalette.orange[800],
      purple: colorPalette.purple[800],
      magenta: colorPalette.magenta[800],
      beige: colorPalette.beige[800],
    },
  },
  border: {
    primaryDefault: colorPalette.grey[1000],
    primaryHover: colorPalette.grey[1100],
    primaryPressed: colorPalette.grey[1100],
    primaryDisabled: colorPalette.grey[400],
    primaryInverse: colorPalette.grey[0],
    secondaryDefault: colorPalette.grey[500],
    secondaryHover: colorPalette.grey[1000],
    secondaryPressed: colorPalette.grey[1000],
    secondaryDisabled: colorPalette.grey[400],
    tertiary: colorPalette.grey[400],
    quaternary: colorPalette.grey[300],
    error: {
      primary: colorPalette.red[700],
      secondary: colorPalette.red[300],
    },
    success: {
      primary: colorPalette.green[700],
      secondary: colorPalette.green[300],
    },
    warning: {
      primary: colorPalette.orange[700],
      secondary: colorPalette.orange[300],
    },
    info: {
      primary: colorPalette.blue[700],
      secondary: colorPalette.blue[300],
    },
  },
  bg: {
    avatar: {
      green: colorPalette.green[300],
      blue: colorPalette.blue[300],
      orange: colorPalette.orange[300],
      purple: colorPalette.purple[300],
      magenta: colorPalette.magenta[300],
      beige: colorPalette.beige[300],
    },
    elevation: {
      primary: colorPalette.grey[0],
      secondary: colorPalette.grey[50],
      tertiary: colorPalette.grey[100],
      quaternary: colorPalette.grey[200],
      overlay: colorPalette.black[1.6],
      overlayBlack34: colorPalette.black[34],
      overlayWhite: whitePalette[60],
      tooltip: colorPalette.grey[900],
    },
    status: {
      error: {
        primary: colorPalette.red[100],
        secondary: colorPalette.red[200],
        tertiary: colorPalette.red[700],
      },
      success: {
        primary: colorPalette.green[100],
        secondary: colorPalette.green[200],
        tertiary: colorPalette.green[600],
      },
      warning: {
        primary: colorPalette.orange[100],
        secondary: colorPalette.orange[200],
        tertiary: colorPalette.orange[600],
      },
      info: {
        primary: colorPalette.blue[100],
        secondary: colorPalette.blue[200],
        tertiary: colorPalette.blue[600],
      },
      alternative: {
        primary: colorPalette.purple[100],
        secondary: colorPalette.purple[200],
        tertiary: colorPalette.purple[300],
      },
    },
    button: {
      primary: {
        default: colorPalette.grey[1000],
        hover: colorPalette.grey[1100],
        pressed: colorPalette.grey[1100],
        disabled: colorPalette.grey[400],
      },
      secondary: {
        default: colorPalette.grey[200],
        hover: colorPalette.grey[300],
        pressed: colorPalette.grey[300],
        disabled: colorPalette.grey[200],
      },
      tertiary: {
        default: colorPalette.grey[0],
        hover: colorPalette.grey[100],
        pressed: colorPalette.grey[100],
        disabled: colorPalette.grey[0],
      },
      custom: {
        default: colorPalette.grey[50],
        hover: colorPalette.grey[300],
        pressed: colorPalette.grey[300],
        selected: colorPalette.grey[300],
        disabled: colorPalette.grey[100],
      },
      destructive: {
        default: colorPalette.red[700],
        hover: colorPalette.red[800],
        pressed: colorPalette.red[800],
        disabled: colorPalette.grey[300],
      },
      switch: {
        default: colorPalette.grey[400],
        hover: colorPalette.grey[500],
        pressed: colorPalette.grey[500],
        disabled: colorPalette.grey[300],
        activeDefault: colorPalette.grey[1000],
        selectedHover: colorPalette.grey[1100],
      },
      close: {
        default: colorPalette.grey[400],
        hover: colorPalette.grey[500],
        pressed: colorPalette.grey[500],
      },
    },
  },
};
